import { useState } from "react";
import { euro, fmtDate } from "../../lib/belege";
import type { Vertrag, VertragPeriode } from "../../lib/lieferantVertraege";
import { STATUS_INFO, ZAHLWEISE_TEXT, fehlermeldung } from "../../lib/lieferantVertraege";
import {
  useBelegKandidaten,
  usePeriodeAendern,
  usePeriodeAnlegen,
  usePeriodeLoeschen,
} from "../../hooks/useLieferantVertraege";
import VertragUnterlagen from "./VertragUnterlagen";

/**
 * Aufgeklappter Vertrag: Stammzeile, Periodenliste mit gerechnetem Status,
 * Periode anlegen, Beleg zuordnen. Der Status kommt fertig vom Server —
 * hier wird nichts nachgerechnet.
 */
export default function VertragDetail({ vertrag }: { vertrag: Vertrag }) {
  const [fehler, setFehler] = useState("");
  const [neuPeriode, setNeuPeriode] = useState("");
  const [neuFaellig, setNeuFaellig] = useState("");
  const [neuSoll, setNeuSoll] = useState("");
  const [zuordnenFuer, setZuordnenFuer] = useState<string | null>(null);

  const anlegen = usePeriodeAnlegen();
  const aendern = usePeriodeAendern();
  const loeschen = usePeriodeLoeschen();
  const kandidaten = useBelegKandidaten(zuordnenFuer ? vertrag.id : null);

  const melden = (e: unknown) => setFehler(fehlermeldung(e));

  function periodeAnlegen() {
    if (!neuPeriode.trim()) { setFehler("Periodenlabel fehlt."); return; }
    setFehler("");
    anlegen.mutate(
      {
        vertragId: vertrag.id,
        periode: neuPeriode.trim(),
        faelligkeit: neuFaellig || null,
        soll_brutto: neuSoll ? Number(neuSoll.replace(",", ".")) : null,
      },
      { onSuccess: () => { setNeuPeriode(""); setNeuFaellig(""); setNeuSoll(""); },
        onError: melden },
    );
  }

  function belegZuordnen(periodeId: string, belegId: string) {
    if (!belegId) return;
    setFehler("");
    aendern.mutate(
      { periodeId, beleg_id: belegId },
      { onSuccess: () => setZuordnenFuer(null), onError: melden },
    );
  }

  return (
    <div className="vtr-detail">
      <div className="vtr-stammzeile">
        {vertrag.kennung && <span>Kennung beim Lieferanten: <strong>{vertrag.kennung}</strong></span>}
        <span>{ZAHLWEISE_TEXT[vertrag.zahlweise]}</span>
        {vertrag.beginn && <span>seit {fmtDate(vertrag.beginn)}</span>}
        {vertrag.ende && <span>bis {fmtDate(vertrag.ende)}</span>}
        {vertrag.kuendigungsfrist && <span>Kündigungsfrist: {vertrag.kuendigungsfrist}</span>}
      </div>
      {vertrag.notizen && <p className="vtr-notizen">{vertrag.notizen}</p>}
      {fehler && <p className="vtr-fehler" role="alert">{fehler}</p>}

      <div className="k-tablewrap">
        <table className="vtr-tabelle k-cardtable">
          <thead>
            <tr>
              <th>Periode</th><th>Fällig</th><th>Soll</th>
              <th>Beleg</th><th>Bank</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {vertrag.perioden.map((p) => (
              <PeriodeZeile
                key={p.id}
                periode={p}
                zuordnenOffen={zuordnenFuer === p.id}
                onZuordnenOeffnen={() => setZuordnenFuer(zuordnenFuer === p.id ? null : p.id)}
                onZuordnen={(belegId) => belegZuordnen(p.id, belegId)}
                kandidaten={zuordnenFuer === p.id ? kandidaten.data ?? [] : []}
                onLoeschen={() => loeschen.mutate(p.id, { onError: melden })}
              />
            ))}
            {!vertrag.perioden.length && (
              <tr><td colSpan={7} className="vtr-leer" data-label="Perioden">Noch keine Perioden erfasst.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="vtr-neu-periode">
        <input
          value={neuPeriode}
          onChange={(e) => setNeuPeriode(e.target.value)}
          placeholder="Periode (z. B. 2026-09 oder Rate 3)"
          aria-label="Neue Periode"
        />
        <input
          type="date"
          value={neuFaellig}
          onChange={(e) => setNeuFaellig(e.target.value)}
          aria-label="Fälligkeit"
        />
        <input
          value={neuSoll}
          onChange={(e) => setNeuSoll(e.target.value)}
          placeholder="Soll € (leer bei Rhythmus)"
          inputMode="decimal"
          aria-label="Soll-Betrag brutto"
        />
        <button type="button" onClick={periodeAnlegen} disabled={anlegen.isPending}>
          Periode anlegen
        </button>
      </div>

      <VertragUnterlagen vertrag={vertrag} />
    </div>
  );
}

function PeriodeZeile({ periode: p, zuordnenOffen, onZuordnenOeffnen, onZuordnen, kandidaten, onLoeschen }: {
  periode: VertragPeriode;
  zuordnenOffen: boolean;
  onZuordnenOeffnen: () => void;
  onZuordnen: (belegId: string) => void;
  kandidaten: { id: string; datum: string; bruttobetrag: number; rechnungsnummer: string | null }[];
  onLoeschen: () => void;
}) {
  const info = STATUS_INFO[p.status];
  return (
    <tr>
      <td data-label="Periode">{p.periode}{p.notiz && <span className="vtr-zeilennotiz"> · {p.notiz}</span>}</td>
      <td data-label="Fällig">{p.faelligkeit ? fmtDate(p.faelligkeit) : "—"}</td>
      <td data-label="Soll">{p.soll_brutto != null ? euro(p.soll_brutto) : "—"}</td>
      <td data-label="Beleg">
        {p.beleg_id
          ? <>{p.beleg_datum ? fmtDate(p.beleg_datum) : ""} · {euro(p.beleg_brutto)}{p.beleg_rechnungsnummer ? ` · ${p.beleg_rechnungsnummer}` : ""}</>
          : "—"}
      </td>
      <td data-label="Bank">
        {p.kontobewegung_id
          ? <>{p.bank_datum ? fmtDate(p.bank_datum) : ""} · {euro(Math.abs(p.bank_betrag ?? 0))}</>
          : "—"}
      </td>
      <td data-label="Status"><span className={`vtr-status vtr-ton-${info.ton}`}>{info.text}</span></td>
      <td data-label="" className="vtr-aktionen">
        {!p.beleg_id && (
          zuordnenOffen ? (
            <select
              aria-label="Beleg auswählen"
              defaultValue=""
              onChange={(e) => onZuordnen(e.target.value)}
            >
              <option value="" disabled>Beleg wählen …</option>
              {kandidaten.map((k) => (
                <option key={k.id} value={k.id}>
                  {fmtDate(k.datum)} · {euro(k.bruttobetrag)}{k.rechnungsnummer ? ` · ${k.rechnungsnummer}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <button type="button" className="vtr-klein" onClick={onZuordnenOeffnen}>Beleg zuordnen</button>
          )
        )}
        {!p.beleg_id && !p.kontobewegung_id && (
          <button type="button" className="vtr-klein" onClick={onLoeschen}>Löschen</button>
        )}
      </td>
    </tr>
  );
}
